import { AA_TEXT, awayFrom, mix, onColor, parseHex, stepUntilReadable, toHex, type Rgb } from "../../shared/contrast.ts";
import { EPK_FONTS, EPK_FONT_WEIGHTS, type EpkFont } from "../../shared/constants.ts";
import { APP_FONT_FAMILY, DEFAULT_FONT_FAMILY, letterSpacingCss, parseFontValue } from "../../shared/fonts.ts";
import { customFontFor, loadCustomFonts } from "./fontSelect.ts";
import { applyLocale } from "./i18n.ts";
import { isUiLocale } from "../../shared/i18n.ts";

/**
 * On-air orange (round 305, the "Liner notes" redesign the owner approved on 2026-09-22), replacing
 * the brand violet - which lives on as the far end of the gradient, because `--accent-2` is this hue
 * turned 95° (style.css). ⚠ This is the APP's default only: the published pages' default accent is
 * `workspaceThemeColor()`'s, and they were deliberately left as they are.
 */
const DEFAULT_ACCENT = "#ff5b35";
const SYSTEM_FONT_STACK = `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
/**
 * What draws when the chosen face does not: the app's own default first, the OS second.
 *
 * A chosen font can fail to arrive for entirely ordinary reasons - a family Google is slow to serve,
 * an upload the owner deleted from another tab - and before this the app fell straight past its own
 * typeface to the operating system's. `APP_FONT_FAMILY` is already loaded (style.css declares it
 * statically), so putting it in the middle costs nothing and keeps a hiccup looking like the product.
 */
function fallbackStack(family: string): string {
	return family === APP_FONT_FAMILY ? SYSTEM_FONT_STACK : `"${APP_FONT_FAMILY}", ${SYSTEM_FONT_STACK}`;
}
const FONT_LINK_ID = "app-font-css";
const FONT_FACE_ID = "app-font-face";

/**
 * The accent the owner last chose. Held because the accent's THREE companions depend on the
 * surfaces under them, so a light/dark flip has to re-derive them even though the color did not
 * change - see `stamp()`.
 */
let currentAccent = DEFAULT_ACCENT;

/**
 * Derive `--accent-hover`, `--accent-text` and `--on-accent` from the accent and the palette that is
 * live right now.
 *
 * ⚠ The reason this cannot be three more literals in `style.css`: the accent is an unconstrained
 * `<input type="color">` in Settings → Appearance. Every other color in the app is one the app
 * chose and can be checked once; this one arrives at runtime, so its relationships have to be
 * COMPUTED or they are guesses. The old code guessed - it added 25 to each channel - and that is
 * wrong in two ways the picker makes reachable: an owner who picks a pale accent got a `#fff` button
 * label at 1.07:1 (invisible), and because the step was always LIGHTER the hover state moved toward
 * the label on a dark accent, so the button got harder to read exactly as you reached for it.
 *
 * The rule that replaces it: pick the label first, then move the hover AWAY from the label. Contrast
 * against the label then rises monotonically, so a hover can never make a button less readable - an
 * invariant rather than a value, which is what a color we did not choose needs.
 */
function deriveAccentTokens() {
	const root = document.documentElement;
	const accent = parseHex(currentAccent) ?? (parseHex(DEFAULT_ACCENT) as Rgb);
	const on = onColor(accent);
	// Away from the LABEL: a white label deepens the hover, an ink label lightens it. 16% is the
	// smallest step that still reads as a state change at button size.
	const hover = mix(accent, awayFrom(on), 0.16);
	// The text role wants the accent readable on the three surfaces it is drawn over. Which way to
	// step is the palette's question, not the accent's: on dark surfaces a color becomes legible by
	// getting lighter, on light ones by getting darker - so ask a surface, not the theme name.
	const style = getComputedStyle(root);
	const surfaces = ["--bg", "--bg2", "--bg3"].map(n => parseHex(style.getPropertyValue(n)) ?? accent);
	const text = stepUntilReadable(accent, awayFrom(surfaces[1] ?? accent), surfaces, AA_TEXT);
	// The same question asked of the CHROME (round 305): the sidebar is ink in both themes, so in light
	// mode the page's --accent-text - darkened to read on paper - is 3:1 on it. Its own answer, against
	// its own two surfaces; in the palettes where the chrome aliases the page, this equals the above.
	const chrome = ["--chrome-bg", "--chrome-bg3"].map(n => parseHex(style.getPropertyValue(n)) ?? accent);
	const chromeText = stepUntilReadable(accent, awayFrom(chrome[0] ?? accent), chrome, AA_TEXT);

	root.style.setProperty("--accent", toHex(accent));
	root.style.setProperty("--accent-hover", toHex(hover));
	root.style.setProperty("--accent-text", toHex(text));
	root.style.setProperty("--chrome-accent-text", toHex(chromeText));
	root.style.setProperty("--on-accent", toHex(on));
}

/**
 * Re-run the derivation without changing the accent - what a PALETTE change needs (§R).
 *
 * The three companions are computed against the surfaces that are live right now, so anything that
 * moves the surfaces under them invalidates all three: a light/dark flip (`stamp()` already calls
 * this), and since §R the high-contrast palettes too. Exported rather than inlined at each call site
 * because forgetting it leaves a link at 3:1 in the palette somebody turned on in order to read.
 */
export function refreshAccentTokens() {
	deriveAccentTokens();
}

export function applyThemeColor(hex: string) {
	currentAccent = parseHex(hex) ? hex : DEFAULT_ACCENT;
	deriveAccentTokens();
}

/**
 * Swap the app's base font (Settings → Appearance → Fonts).
 *
 * Two kinds of font, one setting: an allowlisted Google family loads as a stylesheet, one of the
 * owner's own uploads loads as an `@font-face` pointing at the file the app is serving. Null (or
 * anything unrecognizable) takes both of those away again and drops back to the app default.
 *
 * ⚠ **The default is NOT restored here, and must not be.** Removing the inline `--font-stack` falls
 * through to `public/style.css`'s own value, which is `APP_FONT_FAMILY` followed by the system
 * stack, and the face is declared in that same stylesheet and served from `/fonts/` - so the default
 * draws before this function has run at all, which is the whole point. Setting it back to a literal
 * here would be a second copy of the default that only the JS path can see.
 */
export function applyThemeFont(font: string | null) {
	const link = document.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
	const face = document.getElementById(FONT_FACE_ID) as HTMLStyleElement | null;
	const choice = parseFontValue(font);
	const known = choice?.kind === "custom" || (choice?.kind === "google" && (EPK_FONTS as readonly string[]).includes(choice.family));
	// ⚠ **The DEFAULT family is handled by the branch below, not by the Google branch** (2026-08-25).
	// Work Sans is served from `/fonts/` now and declared in `public/style.css`, so an owner who has
	// explicitly stored "Work Sans" - which looks like any other allowlisted Google choice from here -
	// would otherwise make the app fetch, on every load, the exact face it already has. Falling through
	// to the reset is right in every respect: it removes the inline stack and lands on style.css's own
	// value, which IS this family.
	if (!choice || !known) {
		link?.remove();
		face?.remove();
		document.documentElement.style.removeProperty("--font-stack");
		document.documentElement.style.removeProperty("--font-tracking");
		return;
	}
	// ⚠ **Round 306 split that answer in two.** The app's default is Instrument Sans now, and Work Sans
	// is the PUBLISHED pages' default - still self-hosted by style.css, but no longer what the reset
	// lands on. So a stored "Work Sans" is a real choice again and is honored as one (the owner's own
	// install stores it), and it still never goes to Google for a face this app already serves.
	if (choice.kind === "google" && choice.family === DEFAULT_FONT_FAMILY) {
		link?.remove();
		face?.remove();
		document.documentElement.style.setProperty("--font-stack", `"${DEFAULT_FONT_FAMILY}", ${fallbackStack(DEFAULT_FONT_FAMILY)}`);
		document.documentElement.style.removeProperty("--font-tracking");
		return;
	}

	if (choice.kind === "custom") {
		link?.remove();
		// The family name is only cosmetic here (the face is bound by the @font-face), so the id
		// doubles as it - the app never has to ask the server what the font is called.
		const family = `custom-font-${choice.id}`;
		const style = face ?? document.createElement("style");
		style.id = FONT_FACE_ID;
		style.textContent = `@font-face { font-family: "${family}"; src: url("/api/fonts/${choice.id}/file"); font-display: swap; }`;
		if (!face) document.head.appendChild(style);
		document.documentElement.style.setProperty("--font-stack", `"${family}", ${fallbackStack(family)}`);
		// The font's own tracking, so switching to a tight display face doesn't cramp the whole app
		setAppTracking(customFontFor(font)?.letter_spacing ?? 0);
		return;
	}

	face?.remove();
	document.documentElement.style.removeProperty("--font-tracking");
	const weights = EPK_FONT_WEIGHTS[choice.family as EpkFont] ?? "400;600;700";
	const href = `https://fonts.googleapis.com/css2?family=${choice.family.replace(/ /g, "+")}:wght@${weights}&display=swap`;
	let sheet = link;
	if (!sheet) {
		sheet = document.createElement("link");
		sheet.id = FONT_LINK_ID;
		sheet.rel = "stylesheet";
		document.head.appendChild(sheet);
	}
	if (sheet.href !== href) sheet.href = href;
	document.documentElement.style.setProperty("--font-stack", `"${choice.family}", ${fallbackStack(choice.family)}`);
}

/** Set (or clear) the app-wide letter spacing that rides with a custom base font */
export function setAppTracking(tracking: number) {
	const css = letterSpacingCss(tracking);
	if (css) document.documentElement.style.setProperty("--font-tracking", css);
	else document.documentElement.style.removeProperty("--font-tracking");
}

export function getDefaultAccent() {
	return DEFAULT_ACCENT;
}

export type ThemeMode = "system" | "light" | "dark";

const THEME_MODE_KEY = "theme-mode";
// The OS light/dark signal. We keep one reference so the "system" listener can be added once and
// removed when the workspace pins a fixed mode - a listener per applyThemeMode() call would stack.
const osDark = typeof matchMedia === "function" ? matchMedia("(prefers-color-scheme: dark)") : null;
let osListener: ((e: MediaQueryListEvent) => void) | null = null;

/** Stamp <html data-theme> for `mode`, resolving "system" against the OS right now. */
function stamp(mode: ThemeMode) {
	const resolved = mode === "system" ? (osDark?.matches ? "dark" : "light") : mode;
	document.documentElement.dataset.theme = resolved;
	// The accent's companions are derived against the surfaces, so the palette that just changed
	// under them invalidates all three - including on the OS-driven flip an owner on "system" gets
	// without touching the app. A stale --accent-text here is a link that reads at 3:1 in a theme
	// nobody chose to be in.
	deriveAccentTokens();
}

/**
 * Apply a light/dark mode and cache it for the next load's pre-paint stamp (index.html reads the
 * same key). "system" follows the OS live - so a user who leaves it on system and flips their OS
 * appearance while the app is open sees it follow - which is why the media-query listener is only
 * attached in that mode and torn down when a fixed mode is pinned.
 */
export function applyThemeMode(mode: ThemeMode) {
	stamp(mode);
	try {
		localStorage.setItem(THEME_MODE_KEY, mode);
	} catch {}
	if (osListener && osDark) {
		osDark.removeEventListener("change", osListener);
		osListener = null;
	}
	if (mode === "system" && osDark) {
		osListener = () => stamp("system");
		osDark.addEventListener("change", osListener);
	}
}

export async function initTheme() {
	try {
		const { color, font, mode, locale } = await fetch("/api/settings/theme").then(r => r.json());
		// Reconcile the pre-paint stamp with the server's stored mode (the cache could be stale -
		// another device, or a mode change made elsewhere). Same-value case is a harmless no-op.
		applyThemeMode(mode === "light" || mode === "dark" ? mode : "system");
		// Same reconcile for the UI language: initI18n() already applied the cached/browser locale at
		// boot; this pins the workspace's stored choice if it differs. ⚠ **Awaited**, unlike the color
		// and font lines around it, because applyLocale may now have to FETCH a dictionary - and the
		// two lines below repaint the app, which in a locale we do not hold yet would repaint it in
		// dotted keys. On the overwhelmingly common path (the cache guessed right) it is already here
		// and this await costs a microtask.
		await applyLocale(isUiLocale(locale) ? locale : "system");
		applyThemeColor(color || DEFAULT_ACCENT);
		// A custom base font's tracking lives in the fonts list - load it before applying, or the
		// very first paint would use the font at its raw (possibly cramped) spacing
		if (parseFontValue(font)?.kind === "custom") await loadCustomFonts();
		applyThemeFont(font || null);
	} catch {}
}
